import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePageData } from '../../contexts/DataContext';

// Skeleton loader component
const HeroSkeleton = () => (
  <section className="hero">
    <style>
      {`
        @keyframes skeleton-loading {
          0% { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }
      `}
    </style>

    <div className="hero-text">
      <div
        style={{
          height: "40px",
          width: "60%",
          marginBottom: "12px",
          borderRadius: "4px",
          background: "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 37%, #e0e0e0 63%)",
          backgroundSize: "400% 100%",
          animation: "skeleton-loading 1.4s ease infinite",
        }}
      />

      <div
        style={{
          height: "18px",
          width: "70%",
          marginBottom: "16px",
          borderRadius: "4px",
          background: "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 37%, #e0e0e0 63%)",
          backgroundSize: "400% 100%",
          animation: "skeleton-loading 1.4s ease infinite",
        }}
      />

      <div
        style={{
          height: "40px",
          width: "180px",
          marginBottom: "24px",
          borderRadius: "6px",
          background: "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 37%, #e0e0e0 63%)",
          backgroundSize: "400% 100%",
          animation: "skeleton-loading 1.4s ease infinite",
        }}
      />

      <div className="stats" style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ minWidth: "80px" }}>
            <div
              style={{
                height: "24px",
                width: "60px",
                marginBottom: "6px",
                borderRadius: "4px",
                background: "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 37%, #e0e0e0 63%)",
                backgroundSize: "400% 100%",
                animation: "skeleton-loading 1.4s ease infinite",
              }}
            />
            <div
              style={{
                height: "14px",
                width: "50px",
                borderRadius: "4px",
                background: "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 37%, #e0e0e0 63%)",
                backgroundSize: "400% 100%",
                animation: "skeleton-loading 1.4s ease infinite",
              }}
            />
          </div>
        ))}
      </div>
    </div>

    <div className="hero-image">
      <div
        style={{
          height: "300px",
          width: "100%",
          borderRadius: "8px",
          background: "linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 37%, #e0e0e0 63%)",
          backgroundSize: "400% 100%",
          animation: "skeleton-loading 1.4s ease infinite",
        }}
      />
    </div>
  </section>
);

const HeroSection = () => {
  const { data: homePage, isLoading } = usePageData('homePage');

  // Memoized function to handle image URLs
  const getImageUrl = useMemo(() => {
    return (url) => {
      if (!url) return '/default-hero.jpg';
      
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      
      if (url.startsWith('/')) {
        return `https://resumeslip.com${url}`;
      }
      
      return `https://resumeslip.com/media/home/hero/${url}`;
    };
  }, []);

  // CRITICAL FIX: Show skeleton ONLY when loading AND no data yet
  if (isLoading && !homePage) {
    return <HeroSkeleton />;
  }

  // CRITICAL FIX: Use fallback ONLY when not loading and no data
  const heroData = homePage || {
    hero_title: "Build Your Resume & Slip � Free and Effortless",
    hero_subtitle: "Create professional resumes and slips in minutes. No design skills needed.",
    hero_button_text: "Start Building Now ?",
    hero_image_url: null,
    stats: [
      { value: "100%", label: "Free Forever" },
      { value: "5 Min", label: "Average Time" },
      { value: "8", label: "Templates" }
    ]
  };

  return (
    <section className="hero">
      <div className="hero-text">
        <h1 dangerouslySetInnerHTML={{ __html: heroData.hero_title }} />
        <p>{heroData.hero_subtitle}</p>
        
        <div className="buttons">
          <Link to="/templates">
            <button className="btn-primary">{heroData.hero_button_text}</button>
          </Link>
        </div>
        
        <div className="stats">
          {heroData.stats?.map((stat, index) => (
            <div key={index}>
              <h3>{stat.value}</h3>
              <p>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
      
      <div className="hero-image">
        {heroData.hero_image_url && (
          <img 
            src={getImageUrl(heroData.hero_image_url)} 
            alt="Resume Illustration" 
            loading="eager"
            onError={(e) => {
              e.target.src = '/default-hero.jpg';
              e.target.alt = 'Default hero image';
            }}
          />
        )}
      </div>
    </section>
  );
};

export default HeroSection;